import React, { ReactNode, useCallback, useMemo } from "react";
import { FieldLabel } from "../../models";
import { getFirstDefinedValue } from "../../utils";
import Box from "../box";
import "./styles.css";

export interface DatafieldLabel {
  title?: string;
  colSize?: string; // Can be used for setting width if needed
  items: FieldLabel[];
}

interface DataDisplaySectionProps {
  title?: ReactNode;
  className?: string;
  labels: DatafieldLabel[];
  data?: Record<string, any> | null;
  placeholder?: string;
  withinBox?: boolean;
  footer?: ReactNode;
}

export default function DataDisplaySection({
  title,
  className,
  labels,
  data,
  placeholder = "-",
  withinBox = false,
  footer,
}: DataDisplaySectionProps) {
  const renderFieldData = useCallback(
    (data: Record<string, any>, label: FieldLabel) => {
      let fieldValue: string | number | React.ReactElement | undefined =
        undefined;
      let fieldItem = getFirstDefinedValue(data, label.key);
      if ((fieldItem || typeof fieldItem === "boolean") && label.formatMethod) {
        fieldItem = label.formatMethod(fieldItem);
      }
      if (typeof fieldItem === "string" || typeof fieldItem === "number") {
        fieldValue = label.prefix ? `${label.prefix} ${fieldItem}` : fieldItem;
        if (label.suffix) {
          fieldValue = `${fieldValue} ${label.suffix}`;
        }
      } else if (React.isValidElement(fieldItem)) {
        fieldValue = fieldItem;
      }
      return label.render
        ? label.render(fieldValue ?? placeholder)
        : fieldValue ?? placeholder;
    },
    [placeholder]
  );

  const BodyElem = useMemo(() => {
    return (
      <>
        {data && (
          <div className="datafield-grid">
            {labels.map(({ items, title: columnTitle }, labelIndex) => (
              <div className="datafield-column" key={labelIndex}>
                {columnTitle && <h5 className="mb-4">{columnTitle}</h5>}
                {items.map((eachItemLabel, index) => (
                  <div
                    key={index}
                    className={`datafield-row align-items-${
                      eachItemLabel.alignItem || "start"
                    }`}
                  >
                    <span className="user-details-item-left text-thin">
                      {eachItemLabel.label}
                    </span>
                    <div className="user-details-item-right-wrapper">
                      <span
                        className="user-details-item-right"
                        style={{ whiteSpace: eachItemLabel.wrap }}
                      >
                        {renderFieldData(data, eachItemLabel)}
                      </span>
                      {eachItemLabel.SuffixComponent && (
                        <div className="ml-md-2">
                          {eachItemLabel.SuffixComponent}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {footer}
      </>
    );
  }, [data, footer, labels, renderFieldData]);

  if (withinBox)
    return (
      <Box
        className={className}
        header={title && <h3 className="user-page-title">{title}</h3>}
      >
        {BodyElem}
      </Box>
    );

  return (
    <div className={className}>
      {title && <h3 className="user-page-title mb-4">{title}</h3>}
      {BodyElem}
    </div>
  );
}
